import React from "react";
import { ToastContent, ToastOptions, toast } from "react-toastify";

const Spinner: React.FC = () => (
  <svg className="h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

export const notifyError = (msg: ToastContent, options?: ToastOptions) =>
  toast(msg, { theme: "colored", position: "bottom-right", type: "error", className: "w-full", ...options });

export const notifyWarn = (msg: ToastContent, options?: ToastOptions) =>
  toast(msg, { theme: "colored", position: "bottom-right", type: "warning", className: "w-full", ...options });

export const notifySuccess = (msg: string, options?: ToastOptions) =>
  toast(msg, {
    theme: "colored",
    position: "bottom-right",
    type: "success",
    className: "w-full",
    autoClose: 1000,
    closeButton: false,
    ...options,
  });

export const notifyErrorPersistent = (msg: string) =>
  toast(msg, {
    theme: "colored",
    position: "bottom-right",
    type: "error",
    className: "w-full",
    autoClose: false,
    closeButton: true,
    closeOnClick: false,
  });

export const notifySuccessPersistent = (msg: string) =>
  toast(msg, {
    theme: "colored",
    position: "bottom-right",
    type: "success",
    className: "w-full",
    autoClose: false,
    closeButton: true,
    closeOnClick: false,
  });

// With a toastId, a repeat call replaces the open warning's text instead of stacking a copy
// (react-toastify silently drops a new toast whose id is already on screen).
export const notifyWarnPersistent = (msg: string, toastId?: string) => {
  if (toastId && toast.isActive(toastId)) {
    toast.update(toastId, { render: msg });
    return toastId;
  }
  return notifyWarn(msg, { autoClose: false, closeButton: true, closeOnClick: false, toastId });
};

export const dismissToast = (toastId: string) => toast.dismiss(toastId);

export const notifyConnecting = (msg: string, description?: string) =>
  toast(
    <div className="flex items-start gap-3">
      <Spinner />
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-semibold text-white">{msg}</p>
        {description && <p className="text-sm text-white opacity-90">{description}</p>}
      </div>
    </div>,
    {
      theme: "dark",
      position: "bottom-right",
      type: "default",
      className: "w-full",
      autoClose: 6000,
      closeButton: false,
      closeOnClick: false,
      icon: false,
    },
  );

export const notifyEhrConnected = () =>
  toast(
    <div className="flex flex-col gap-0.5">
      <p className="text-sm font-semibold text-white">EHR connected</p>
      <p className="text-sm text-white opacity-90">Your EHR is connected and actions are now available.</p>
    </div>,
    {
      theme: "dark",
      position: "bottom-right",
      type: "default",
      className: "w-full",
      autoClose: 5000,
    },
  );

const IMPORTING_TOAST_ID = "importing-client-data";

export const notifyImporting = () =>
  toast(
    <div className="flex items-start gap-3">
      <Spinner />
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-semibold text-white">Importing client data</p>
        <p className="text-sm text-white opacity-90">
          This may take up to 8-10 minutes. Feel free to continue working, we&apos;ll notify you when the import is
          complete.
        </p>
      </div>
    </div>,
    {
      toastId: IMPORTING_TOAST_ID,
      theme: "dark",
      position: "bottom-right",
      type: "default",
      className: "w-full",
      autoClose: 6000,
      closeButton: false,
      closeOnClick: false,
      icon: false,
    },
  );

export const dismissImportingToast = () => toast.dismiss(IMPORTING_TOAST_ID);

export const notifyImportSuccess = () =>
  toast(
    <div className="flex flex-col gap-0.5">
      <p className="text-sm font-semibold text-white">Successfully imported client data</p>
      <p className="text-sm text-white opacity-90">Your client data is now imported and available to use.</p>
    </div>,
    {
      theme: "dark",
      position: "bottom-right",
      type: "default",
      className: "w-full",
      autoClose: 5000,
    },
  );

export const REFRESHING_TOAST_ID = "refreshing-client-list";

export const notifyRefreshing = () =>
  toast(
    <div className="flex items-start gap-3">
      <Spinner />
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-semibold text-white">Refreshing your client list</p>
        <p className="text-sm text-white opacity-90">
          This may take a few minutes. Feel free to continue working, we&apos;ll notify you when the refresh is
          complete.
        </p>
      </div>
    </div>,
    {
      toastId: REFRESHING_TOAST_ID,
      theme: "dark",
      position: "bottom-right",
      type: "default",
      className: "w-full",
      autoClose: 6000,
      closeButton: false,
      closeOnClick: false,
      icon: false,
    },
  );

export const dismissRefreshingToast = () => toast.dismiss(REFRESHING_TOAST_ID);

export const notifyRefreshSuccess = () =>
  toast(
    <div className="flex flex-col gap-0.5">
      <p className="text-sm font-semibold text-white">Client list refreshed</p>
      <p className="text-sm text-white opacity-90">Your client list is up to date.</p>
    </div>,
    {
      theme: "dark",
      position: "bottom-right",
      type: "default",
      className: "w-full",
      autoClose: 5000,
    },
  );
